import { CSSObject } from "@emotion/react";
import {
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { TFunction } from "i18next";
import { GroupBase, StylesConfig } from "react-select";
import {
  IDateRangePreset,
  UserRole,
  TCurrency,
  StatusOptionData,
} from "../interfaces";
import { PlatformConfig } from "../features/Configurations/Integration/interface";

export const SIDEBAR = {
  EXPANDED_WIDTH: "270px",
  COLLAPSED_WIDTH: "80px",
} as const;

export const API_CONFIG = {
  baseUrl: import.meta.env.VITE_API_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
  path: {
    login: "login",
    viewAs: "admin/view-as",
    logout: "users/logout",
    register: "register",
    users: "users",
    refreshToken: "token/refresh",
    me: "users/me",
    userActivity: "user-activity",
    permissions: "permissions",
    categorizedPermissions: "permissions/category",
    onboarding: "onboarding",
    oauthCallback: "oauth/callback",
    teamMembers: "team-members",
    teamMembersSessions: "team-members/sessions",
    acceptInvite: "invite-user-accept",
    changePassword: "change-password",
    forgotPassword: "forgot-password",
    resetPassword: "password-reset-confirm",
    insertApps: "insert-apps",
    getApps: "apps",
    roles: "roles",
    getAppDetails: "apps/details",
    getAppName: "apps/get-app-name",
    getAppsChart: "apps/chart",
    getAppsCountryChart: "apps/country-chart",
    dashboard: "apps/dashboard",
    campaigns: "campaigns",
    getAppBidManagement: "apps/get-campaign-bid-data",
    getCampaignHistory: "get-campaign-history",
    updateCampaignBid: "update-campaign-bid",
    updateCampaignBudget: "campaigns/budget",
    campaignsStatus: "campaigns/status",
    getCampaignCountryCost: "get-campaign-country-cost",
    googleOAuth: "google-auth",
    googleOAuthCallback: "google-auth-callback",
    connectedAccounts: "connected-accounts",
    userDetailsFromEmail: "users/email",
    syncCampaigns: "campaigns/sync",
    syncApps: "apps/sync",
    disconnectAccount: "disconnect-account",
    countryReports: "apps/country-reports",
    // Integration
    trackingIntegration: "tracking-integration",
    socialAccountStatus: "tracking-integration/social-account-status",
    latestSocialAccountStatus: "tracking-integration/latest-social-account-status",
    sendIncompleteIntegrationReminder: "tracking-integration/send-incomplete-reminder",
    sendIntegrationCompletionConfirmation: "tracking-integration/send-completion-confirmation",
    googleAdsAccounts: "google-ads-accounts",
    googleAdsSubAccounts: "get-sub-account-list",
    googleAdsSaveAccount: "google-ads-save-account",
    deleteSocialAccount: "social-account",
    appleAdsAccounts: "insert-apple-iap",
    appleAdsSpending: "apple-ads",
    activateAppleAds: "activate-apple-ads",
    getGrowthIndicator: "get-growth-indicator",
    updateGrowthIndicator: "update-growth-indicator",
    googleIapInsert: "connect-google-iap",
    supportTickets: "support-tickets",
    googleIapOrders: "google-iap/orders",
    googleIapAppSummary: "google-iap/apps/summary",
    googleIapAppOrders: "google-iap/apps/:slug/orders",
    googleIapAppOrdersConsolidated: "google-iap/apps/order",
  },
};

export const DEFAULT_ITEMS_PER_PAGE = 50;

export const PLATFORM_ICON_MAP: Record<string, string> = {
  google_ads: "https://assets.apprevbooster.com/integrations/googleAds.png",
  google_admob: "https://assets.apprevbooster.com/integrations/googleAdMob.svg",
  facebook:
    "https://assets.apprevbooster.com/integrations/meta.png",
  apple_ads: "https://assets.apprevbooster.com/integrations/appStore.png",
  apple_iap: "https://assets.apprevbooster.com/integrations/appleIcon.svg",
  google_iap:
    "https://assets.apprevbooster.com/integrations/playStore.webp",
};

export const PLATFORM_OPTIONS = [
  { label: "GoogleAds", value: "google_ads" },
  { label: "AdMob", value: "google_admob" },
  { label: "AppleAds", value: "apple_ads" },
  { label: "AppleIAP", value: "apple_iap" },
  { label: "GoogleIAP", value: "google_iap" },
  // { label: "FaceBook", value: "facebook" },
];

export const PLATFORM_LABEL_MAPPER: Record<string, string> = {
  google_ads: "Google Ads",
  google_admob: "Google AdMob",
  apple_ads: "Apple Ads",
  apple_iap: "Apple IAP",
  google_iap: "Google IAP",
  meta: "Meta",
};

export const DEFAULT_AVATAR =
  "https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541";

export const DEFAULT_AVATAR_FOR_DELETED_USER =
  "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/deleted_user.png";

export const LANGUAGES_OPTIONS = [
  { code: "en", name: "English", flagCode: "US" },
  { code: "de", name: "Deutsch", flagCode: "DE" },
  { code: "fr", name: "Français", flagCode: "FR" },
  { code: "es", name: "Español", flagCode: "ES" },
  { code: "ja", name: "日本語", flagCode: "JP" },
];

export const APP_PLATFORM_LOGO_MAP: Record<string, string> = {
  android: "https://assets.apprevbooster.com/integrations/googleAdMob.svg", // Android logo
  ios: "https://assets.apprevbooster.com/integrations/appleIcon.svg", // Apple logo
  google: "https://assets.apprevbooster.com/integrations/googleIcon.svg",
  apple: "https://assets.apprevbooster.com/integrations/appleIcon.svg",
  playStore: "https://assets.apprevbooster.com/integrations/playStoreIcon.svg",
  google_ads: "https://assets.apprevbooster.com/integrations/googleAds.png",
  google_admob: "https://assets.apprevbooster.com/integrations/googleAdMob.svg",
  app_store: "https://assets.apprevbooster.com/integrations/appStore.png",
  meta: "https://assets.apprevbooster.com/integrations/meta.png",
};

export const PLANS = [
  {
    name: "Free",
    priceDisplay: "₹0/mo",
    price: 0,
    features: ["Basic Features", "Limited Support"],
    unavailable: ["Advanced Analytics", "Priority Support"],
    buttonText: "Get Started",
    buttonVariant: "active",
  },
  {
    name: "Basic",
    priceDisplay: "₹9/mo",
    price: 9,
    features: ["All Free Features", "Advanced Analytics"],
    unavailable: ["Priority Support"],
    buttonText: "Subscribe",
    buttonVariant: "default",
  },
  {
    name: "Pro",
    priceDisplay: "₹19/mo",
    price: 19,
    features: ["All Basic Features", "Priority Support", "Customization"],
    unavailable: [],
    buttonText: "Go Pro",
    buttonVariant: "primary",
  },
];

export const ROLES: UserRole[] = ["super_admin", "admin", "sub_admin"];

export const CURRENCIES: TCurrency[] = [
  "AED", "AFN", "ALL", "AMD", "ANG", "AOA", "ARS", "AUD", "AWG", "AZN",
  "BAM", "BBD", "BDT", "BGN", "BHD", "BIF", "BMD", "BND", "BOB", "BRL",
  "BSD", "BTN", "BWP", "BYN", "BZD", "CAD", "CDF", "CHF", "CLP", "CNY",
  "COP", "CRC", "CUP", "CVE", "CZK", "DJF", "DKK", "DOP", "DZD", "EGP",
  "ERN", "ETB", "EUR", "FJD", "FKP", "GBP", "GEL", "GHS", "GIP", "GMD",
  "GNF", "GTQ", "GYD", "HKD", "HNL", "HRK", "HTG", "HUF", "IDR", "ILS",
  "INR", "IQD", "IRR", "ISK", "JMD", "JOD", "JPY", "KES", "KGS", "KHR",
  "KMF", "KPW", "KRW", "KWD", "KYD", "KZT", "LAK", "LBP", "LKR", "LRD",
  "LSL", "LYD", "MAD", "MDL", "MGA", "MKD", "MMK", "MNT", "MOP", "MRU",
  "MUR", "MVR", "MWK", "MXN", "MYR", "MZN", "NAD", "NGN", "NIO", "NOK",
  "NPR", "NZD", "OMR", "PAB", "PEN", "PGK", "PHP", "PKR", "PLN", "PYG",
  "QAR", "RON", "RSD", "RUB", "RWF", "SAR", "SBD", "SCR", "SDG", "SEK",
  "SGD", "SHP", "SLE", "SLL", "SOS", "SRD", "SSP", "STN", "SYP", "SZL",
  "THB", "TJS", "TMT", "TND", "TOP", "TRY", "TTD", "TWD", "TZS", "UAH",
  "UGX", "USD", "UYU", "UZS", "VES", "VND", "VUV", "WST", "XAF", "XCD",
  "XOF", "XPF", "YER", "ZAR", "ZMW", "ZWL",
];

export const CURRENCIES_OPTIONS = [
  { code: "AED", symbol: "د.إ", name: "United Arab Emirates Dirham" },
  { code: "AFN", symbol: "؋", name: "Afghan Afghani" },
  { code: "ALL", symbol: "L", name: "Albanian Lek" },
  { code: "AMD", symbol: "֏", name: "Armenian Dram" },
  { code: "ANG", symbol: "ƒ", name: "Netherlands Antillean Guilder" },
  { code: "AOA", symbol: "Kz", name: "Angolan Kwanza" },
  { code: "ARS", symbol: "$", name: "Argentine Peso" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "AWG", symbol: "ƒ", name: "Aruban Florin" },
  { code: "AZN", symbol: "₼", name: "Azerbaijani Manat" },
  { code: "BAM", symbol: "KM", name: "Bosnia-Herzegovina Convertible Mark" },
  { code: "BBD", symbol: "$", name: "Barbadian Dollar" },
  { code: "BDT", symbol: "৳", name: "Bangladeshi Taka" },
  { code: "BGN", symbol: "лв", name: "Bulgarian Lev" },
  { code: "BHD", symbol: ".د.ب", name: "Bahraini Dinar" },
  { code: "BIF", symbol: "FBu", name: "Burundian Franc" },
  { code: "BMD", symbol: "$", name: "Bermudan Dollar" },
  { code: "BND", symbol: "$", name: "Brunei Dollar" },
  { code: "BOB", symbol: "Bs.", name: "Bolivian Boliviano" },
  { code: "BRL", symbol: "R$", name: "Brazilian Real" },
  { code: "BSD", symbol: "$", name: "Bahamian Dollar" },
  { code: "BTN", symbol: "Nu.", name: "Bhutanese Ngultrum" },
  { code: "BWP", symbol: "P", name: "Botswanan Pula" },
  { code: "BYN", symbol: "Br", name: "Belarusian Ruble" },
  { code: "BZD", symbol: "BZ$", name: "Belize Dollar" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "CDF", symbol: "FC", name: "Congolese Franc" },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc" },
  { code: "CLP", symbol: "$", name: "Chilean Peso" },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
  { code: "COP", symbol: "$", name: "Colombian Peso" },
  { code: "CRC", symbol: "₡", name: "Costa Rican Colón" },
  { code: "CUP", symbol: "$", name: "Cuban Peso" },
  { code: "CVE", symbol: "$", name: "Cape Verdean Escudo" },
  { code: "CZK", symbol: "Kč", name: "Czech Koruna" },
  { code: "DJF", symbol: "Fdj", name: "Djiboutian Franc" },
  { code: "DKK", symbol: "kr", name: "Danish Krone" },
  { code: "DOP", symbol: "RD$", name: "Dominican Peso" },
  { code: "DZD", symbol: "دج", name: "Algerian Dinar" },
  { code: "EGP", symbol: "E£", name: "Egyptian Pound" },
  { code: "ERN", symbol: "Nfk", name: "Eritrean Nakfa" },
  { code: "ETB", symbol: "Br", name: "Ethiopian Birr" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "FJD", symbol: "$", name: "Fijian Dollar" },
  { code: "FKP", symbol: "£", name: "Falkland Islands Pound" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "GEL", symbol: "₾", name: "Georgian Lari" },
  { code: "GHS", symbol: "GH₵", name: "Ghanaian Cedi" },
  { code: "GIP", symbol: "£", name: "Gibraltar Pound" },
  { code: "GMD", symbol: "D", name: "Gambian Dalasi" },
  { code: "GNF", symbol: "FG", name: "Guinean Franc" },
  { code: "GTQ", symbol: "Q", name: "Guatemalan Quetzal" },
  { code: "GYD", symbol: "$", name: "Guyanaese Dollar" },
  { code: "HKD", symbol: "HK$", name: "Hong Kong Dollar" },
  { code: "HNL", symbol: "L", name: "Honduran Lempira" },
  { code: "HRK", symbol: "kn", name: "Croatian Kuna" },
  { code: "HTG", symbol: "G", name: "Haitian Gourde" },
  { code: "HUF", symbol: "Ft", name: "Hungarian Forint" },
  { code: "IDR", symbol: "Rp", name: "Indonesian Rupiah" },
  { code: "ILS", symbol: "₪", name: "Israeli New Shekel" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "IQD", symbol: "ع.د", name: "Iraqi Dinar" },
  { code: "IRR", symbol: "﷼", name: "Iranian Rial" },
  { code: "ISK", symbol: "kr", name: "Icelandic Króna" },
  { code: "JMD", symbol: "J$", name: "Jamaican Dollar" },
  { code: "JOD", symbol: "د.ا", name: "Jordanian Dinar" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
  { code: "KGS", symbol: "с", name: "Kyrgystani Som" },
  { code: "KHR", symbol: "៛", name: "Cambodian Riel" },
  { code: "KMF", symbol: "CF", name: "Comorian Franc" },
  { code: "KPW", symbol: "₩", name: "North Korean Won" },
  { code: "KRW", symbol: "₩", name: "South Korean Won" },
  { code: "KWD", symbol: "د.ك", name: "Kuwaiti Dinar" },
  { code: "KYD", symbol: "$", name: "Cayman Islands Dollar" },
  { code: "KZT", symbol: "₸", name: "Kazakhstani Tenge" },
  { code: "LAK", symbol: "₭", name: "Laotian Kip" },
  { code: "LBP", symbol: "ل.ل", name: "Lebanese Pound" },
  { code: "LKR", symbol: "₨", name: "Sri Lankan Rupee" },
  { code: "LRD", symbol: "$", name: "Liberian Dollar" },
  { code: "LSL", symbol: "M", name: "Lesotho Loti" },
  { code: "LYD", symbol: "ل.د", name: "Libyan Dinar" },
  { code: "MAD", symbol: "MAD", name: "Moroccan Dirham" },
  { code: "MDL", symbol: "L", name: "Moldovan Leu" },
  { code: "MGA", symbol: "Ar", name: "Malagasy Ariary" },
  { code: "MKD", symbol: "ден", name: "Macedonian Denar" },
  { code: "MMK", symbol: "K", name: "Myanmar Kyat" },
  { code: "MNT", symbol: "₮", name: "Mongolian Tugrik" },
  { code: "MOP", symbol: "P", name: "Macanese Pataca" },
  { code: "MRU", symbol: "UM", name: "Mauritanian Ouguiya" },
  { code: "MUR", symbol: "₨", name: "Mauritian Rupee" },
  { code: "MVR", symbol: ".ރ", name: "Maldivian Rufiyaa" },
  { code: "MWK", symbol: "MK", name: "Malawian Kwacha" },
  { code: "MXN", symbol: "$", name: "Mexican Peso" },
  { code: "MYR", symbol: "RM", name: "Malaysian Ringgit" },
  { code: "MZN", symbol: "MT", name: "Mozambican Metical" },
  { code: "NAD", symbol: "$", name: "Namibian Dollar" },
  { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
  { code: "NIO", symbol: "C$", name: "Nicaraguan Córdoba" },
  { code: "NOK", symbol: "kr", name: "Norwegian Krone" },
  { code: "NPR", symbol: "₨", name: "Nepalese Rupee" },
  { code: "NZD", symbol: "NZ$", name: "New Zealand Dollar" },
  { code: "OMR", symbol: "﷼", name: "Omani Rial" },
  { code: "PAB", symbol: "B/.", name: "Panamanian Balboa" },
  { code: "PEN", symbol: "S/.", name: "Peruvian Sol" },
  { code: "PGK", symbol: "K", name: "Papua New Guinean Kina" },
  { code: "PHP", symbol: "₱", name: "Philippine Peso" },
  { code: "PKR", symbol: "₨", name: "Pakistani Rupee" },
  { code: "PLN", symbol: "zł", name: "Polish Zloty" },
  { code: "PYG", symbol: "Gs", name: "Paraguayan Guarani" },
  { code: "QAR", symbol: "﷼", name: "Qatari Rial" },
  { code: "RON", symbol: "lei", name: "Romanian Leu" },
  { code: "RSD", symbol: "дин.", name: "Serbian Dinar" },
  { code: "RUB", symbol: "₽", name: "Russian Ruble" },
  { code: "RWF", symbol: "RF", name: "Rwandan Franc" },
  { code: "SAR", symbol: "﷼", name: "Saudi Riyal" },
  { code: "SBD", symbol: "$", name: "Solomon Islands Dollar" },
  { code: "SCR", symbol: "₨", name: "Seychellois Rupee" },
  { code: "SDG", symbol: "ج.س.", name: "Sudanese Pound" },
  { code: "SEK", symbol: "kr", name: "Swedish Krona" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { code: "SHP", symbol: "£", name: "St. Helena Pound" },
  { code: "SLE", symbol: "Le", name: "Sierra Leonean Leone" },
  { code: "SLL", symbol: "Le", name: "Sierra Leonean Leone" },
  { code: "SOS", symbol: "S", name: "Somali Shilling" },
  { code: "SRD", symbol: "$", name: "Surinamese Dollar" },
  { code: "SSP", symbol: "£", name: "South Sudanese Pound" },
  { code: "STN", symbol: "Db", name: "São Tomé & Príncipe Dobra" },
  { code: "SYP", symbol: "£", name: "Syrian Pound" },
  { code: "SZL", symbol: "L", name: "Swazi Lilangeni" },
  { code: "THB", symbol: "฿", name: "Thai Baht" },
  { code: "TJS", symbol: "SM", name: "Tajikistani Somoni" },
  { code: "TMT", symbol: "T", name: "Turkmenistani Manat" },
  { code: "TND", symbol: "د.ت", name: "Tunisian Dinar" },
  { code: "TOP", symbol: "T$", name: "Tongan Paʻanga" },
  { code: "TRY", symbol: "₺", name: "Turkish Lira" },
  { code: "TTD", symbol: "TT$", name: "Trinidad & Tobago Dollar" },
  { code: "TWD", symbol: "NT$", name: "New Taiwan Dollar" },
  { code: "TZS", symbol: "TSh", name: "Tanzanian Shilling" },
  { code: "UAH", symbol: "₴", name: "Ukrainian Hryvnia" },
  { code: "UGX", symbol: "USh", name: "Ugandan Shilling" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "UYU", symbol: "$U", name: "Uruguayan Peso" },
  { code: "UZS", symbol: "so'm", name: "Uzbekistani Som" },
  { code: "VES", symbol: "Bs.S", name: "Venezuelan Bolívar" },
  { code: "VND", symbol: "₫", name: "Vietnamese Dong" },
  { code: "VUV", symbol: "VT", name: "Vanuatu Vatu" },
  { code: "WST", symbol: "WS$", name: "Samoan Tala" },
  { code: "XAF", symbol: "FCFA", name: "Central African CFA Franc" },
  { code: "XCD", symbol: "$", name: "East Caribbean Dollar" },
  { code: "XOF", symbol: "CFA", name: "West African CFA Franc" },
  { code: "XPF", symbol: "₣", name: "CFP Franc" },
  { code: "YER", symbol: "﷼", name: "Yemeni Rial" },
  { code: "ZAR", symbol: "R", name: "South African Rand" },
  { code: "ZMW", symbol: "ZK", name: "Zambian Kwacha" },
  { code: "ZWL", symbol: "$", name: "Zimbabwean Dollar" },
];

export const customSelectStyles = <T,>(): StylesConfig<
  T,
  boolean,
  GroupBase<T>
> => ({
  control: (base: CSSObject, state: { isFocused: boolean }) => ({
    ...base,
    minHeight: "32px",
    borderRadius: "0.5rem",
    borderColor: state.isFocused ? "transparent" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 2px #9ca3af" : "none",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "16px",
    color: "#111827",
    "&:hover": {
      borderColor: state.isFocused ? "transparent" : "#d1d5db",
    },
  }),
  menu: (base: CSSObject) => ({
    ...base,
    zIndex: "20",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "16px",
    color: "#111827",
  }),
  option: (
    base: CSSObject,
    state: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...base,
    backgroundColor: state.isSelected ? "#f3f4f6" : "transparent",
    color: "#111827",
    fontSize: "14px",
    fontFamily: "Inter, sans-serif",
    fontWeight: "500",
    lineHeight: "16px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f3f4f6",
    },
  }),
  multiValue: (base: CSSObject) => ({
    ...base,
    backgroundColor: "#e5e7eb",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "16px",
    color: "#111827",
  }),
  multiValueLabel: (base: CSSObject) => ({
    ...base,
    color: "#111827",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "16px",
  }),
  multiValueRemove: (base: CSSObject) => ({
    ...base,
    color: "#111827",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "16px",
    "&:hover": {
      backgroundColor: "#d1d5db",
      color: "#111827",
    },
  }),
  clearIndicator: (base: CSSObject) => ({
    ...base,
    cursor: "pointer",
  }),
  singleValue: (base: CSSObject) => ({
    ...base,
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "16px",
    color: "#111827",
  }),
  valueContainer: (base: CSSObject) => ({
    ...base,
    cursor: "pointer",
  }),
});

export const BIDDING_STRATEGY_OPTIONS = [
  { value: "TARGET_CPA", label: "TARGET CPA" },
  { value: "TARGET_ROAS", label: "TARGET ROAS" },
];

export const BIDDING_STRATEGY_LABEL_MAPPER: { [key: string]: string } = {
  TARGET_CPA: "Target CPA",
  TARGET_ROAS: "Target ROAS",
};

export const getPresets = (t: TFunction<"translation", undefined>) => {
  const yesterday = subDays(new Date(), 1);
  return [
    {
      id: "yesterday",
      label: t("common.yesterday"),
      getRange: () => [yesterday, yesterday],
    },
    {
      id: "last7Days",
      label: t("common.last7Days"),
      getRange: () => [subDays(yesterday, 6), yesterday],
    },
    {
      id: "last30Days",
      label: t("common.last30Days"),
      getRange: () => [subDays(yesterday, 29), yesterday],
    },
    {
      id: "thisWeek",
      label: t("common.thisWeek"),
      getRange: () => {
        const weekEnd = yesterday;
        const weekStart = startOfWeek(weekEnd);
        return [weekStart, weekEnd];
      },
    },
    {
      id: "lastWeek",
      label: t("common.lastWeek"),
      getRange: () => {
        const lastWeekEnd = subDays(startOfWeek(yesterday), 1);
        const lastWeekStart = startOfWeek(lastWeekEnd);
        return [lastWeekStart, lastWeekEnd];
      },
    },
    {
      id: "thisMonth",
      label: t("common.thisMonth"),
      getRange: () => {
        const monthEnd = yesterday;
        const monthStart = startOfMonth(monthEnd);
        return [monthStart, monthEnd];
      },
    },
    {
      id: "lastMonth",
      label: t("common.lastMonth"),
      getRange: () => {
        const lastMonth = subMonths(yesterday, 1);
        return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
      },
    },
    {
      id: "thisYear",
      label: t("common.thisYear"),
      getRange: () => {
        const yearEnd = yesterday;
        const yearStart = startOfYear(yearEnd);
        return [yearStart, yearEnd];
      },
    },
    {
      id: "lastYear",
      label: t("common.lastYear"),
      getRange: () => {
        const lastYear = subYears(yesterday, 1);
        return [startOfYear(lastYear), endOfYear(lastYear)];
      },
    },
  ] as IDateRangePreset[];
};

export const DEFAULT_SELECTED_ACCOUNT = {
  label: "All",
  value: "all",
  name: "All",
  email: "",
  id: 0,
  slug: "all",
  last_sync_at: "",
  min_date: "",
  profile_picture: null,
};

export enum PERMISSION_MAPPER {
  SUPER_ADMIN = "super_admin",
  SCHEMA_LEVEL_ACCESS = "schema_level_access",
  DASHBOARD_READ = "dashboard_read",
  APP_COUNTRY_REPORTS_READ = "app_country_reports_read",
  BID_MANAGEMENT_READ = "bid_management_read",
  BID_MANAGEMENT_UPDATE = "bid_management_update",
  APP_READ = "app_read",
  APP_FIND_ONE = "app_find_one",
  APP_CHART_READ = "app_chart_read",
  APP_DETAIL_CHART_READ = "app_detail_chart_read",
  CAMPAIGN_COUNTRY_COST_READ = "campaign_country_cost_read",
  CAMPAIGN_FIND_ONE = "campaign_find_one",
  CAMPAIGN_READ = "campaign_read",
  TEAM_MANAGEMENT_CREATE = "team_management_create",
  TEAM_MANAGEMENT_READ = "team_management_read",
  TEAM_MANAGEMENT_UPDATE = "team_management_update",
  TEAM_MANAGEMENT_DELETE = "team_management_delete",
  ROLE_MANAGEMENT_CREATE = "role_management_create",
  ROLE_MANAGEMENT_READ = "role_management_read",
  ROLE_MANAGEMENT_UPDATE = "role_management_update",
  ROLE_MANAGEMENT_DELETE = "role_management_delete",
  INTEGRATION_CREATE = "integration_create",
  INTEGRATION_READ = "integration_read",
  INTEGRATION_UPDATE = "integration_update",
  INTEGRATION_DELETE = "integration_delete",
  SETTINGS_READ = "settings_read",
  USER_ACTIVITY_READ = "user_activity_read",
  SESSION_ACTIVITY_READ = "session_activity_read",
  INTEGRATION_ADDON_CREATE = "addon_integration_create",
  INTEGRATION_ADDON_DELETE = "addon_integration_delete",
  ORDER_READ = "order_read",
  ORDER_EXPORT = "order_export",
}

export const ACTION_TYPE_META: Record<
  string,
  { label: string; color: string; showDiff: boolean }
> = {
  LOGIN: { label: "Login", color: "green", showDiff: false },
  LOGOUT: { label: "Logout", color: "red", showDiff: false },
  SIGN_UP: { label: "Sign Up", color: "green", showDiff: false },
  PROFILE_UPDATE: { label: "Profile Update", color: "blue", showDiff: true },
  INVITE_USER_ACCEPT: {
    label: "Invite Accepted",
    color: "indigo",
    showDiff: false,
  },
  INVITE_USER_EMAIL_SENT: {
    label: "Invite Email Sent",
    color: "indigo",
    showDiff: false,
  },
  INVITE_USER_DELETE: {
    label: "Invite Deleted",
    color: "red",
    showDiff: false,
  },
  RESEND_INVITE: { label: "Resend Invite", color: "indigo", showDiff: false },
  USER_DELETE: { label: "User Deleted", color: "red", showDiff: false },
  PASSWORD_RESET_REQUEST: {
    label: "Password Reset Requested",
    color: "yellow",
    showDiff: false,
  },
  PASSWORD_RESET_COMPLETE: {
    label: "Password Reset Complete",
    color: "green",
    showDiff: false,
  },
  PASSWORD_CHANGE: {
    label: "Password Changed",
    color: "yellow",
    showDiff: false,
  },
  USER_ROLE_CHANGE: {
    label: "User Role Changed",
    color: "blue",
    showDiff: true,
  },
  ROLE_CREATE: { label: "Role Created", color: "green", showDiff: false },
  ROLE_UPDATE: { label: "Role Updated", color: "blue", showDiff: true },
  ROLE_DELETE: { label: "Role Deleted", color: "red", showDiff: false },
  DASHBOARD_PREFERENCE_UPDATE: {
    label: "Dashboard Pref Update",
    color: "blue",
    showDiff: true,
  },
  APP_LIST_PREFERENCE_UPDATE: {
    label: "App List Pref Update",
    color: "blue",
    showDiff: true,
  },
  APP_DETAIL_PREFERENCE_UPDATE: {
    label: "App Detail Pref Update",
    color: "blue",
    showDiff: true,
  },
  APP_COUNTRY_REPORT_PREFERENCE_UPDATE: {
    label: "Country Report Pref Update",
    color: "blue",
    showDiff: true,
  },
  BID_CHANGE: { label: "Bid Change", color: "purple", showDiff: true },
  INTEGRATION_START: {
    label: "Integration Started",
    color: "indigo",
    showDiff: false,
  },
  INTEGRATION_COMPLETE: {
    label: "Integration Complete",
    color: "green",
    showDiff: false,
  },
  INTEGRATION_DELETE: {
    label: "Integration Deleted",
    color: "red",
    showDiff: false,
  },
  INVITE_USER_UPDATE: {
    label: "Invite Updated",
    color: "gray",
    showDiff: true,
  },
  USER_DEACTIVATED: {
    label: "User Deactivated",
    color: "gray",
    showDiff: false,
  },
  USER_ACTIVATED: {
    label: "User Activated",
    color: "green",
    showDiff: false,
  },
  ADDON_ACCOUNT_DELETE: {
    label: "Account Deleted",
    color: "red",
    showDiff: false,
  },
  ONGOING_ACCOUNT_DELETE: {
    label: "Ongoing Account Delete",
    color: "gray",
    showDiff: false,
  },
  ADDON_ACCOUNT_INTEGRATION_START: {
    label: "Addon Account Integration Started",
    color: "blue",
    showDiff: false,
  },
  ADDON_ACCOUNT_INTEGRATION_CANCEL: {
    label: "Addon Account Integration Cancelled",
    color: "red",
    showDiff: false,
  },
  ADDON_ACCOUNT_INTEGRATION_COMPLETE: {
    label: "Addon Account Integration Complete",
    color: "green",
    showDiff: false,
  },
  ENTIRE_TRACKING_DELETE: {
    label: "Entire Tracking Deleted",
    color: "red",
    showDiff: false,
  },
};

export const IS_OWNER_FILTER_OPTIONS = [
  { value: "all", label: "All Team Members" },
  { value: "owner", label: "Owner" },
  { value: "not_owner", label: "Not Owner" },
];

export enum LOCAL_STORAGE_KEYS {
  ADD_ON = "addon",
  PLATFORM = "platform",
  INTEGRATING_PLATFORM = "integrating_platform",
  SHOW_SYNC_PROGRESS = "show_sync_progress",
  TASK_ID = "task_id",
  TRACKING_ID = "tracking_id",
  DATA_RANGE_FROM = "data_range_from",
  DATA_RANGE_TO = "data_range_to",
  DATA_RANGE_PRESET = "data_range_preset",
  WIZARD_STEP = "wizard_step",
  INTEGRATION_MODE = "integration_mode",
  EDIT_PROGRESS_STATE = "edit_progress_state",
}

export enum CAMPAIGN_STATUS {
  UNSPECIFIED = "Unspecified",
  UNKNOWN = "Unknown",
  ENABLED = "Enabled",
  PAUSED = "Paused",
  REMOVED = "Removed",
}

export const STATUS_OPTIONS: StatusOptionData[] = [
  { label: "All Status", value: "all", color: "gray" },
  { label: "Growth (TN/TR > 0.5)", value: "success", color: "green" },
  { label: "Stable (0 ≤ TN/TR ≤ 0.5)", value: "warning", color: "yellow" },
  { label: "Declining (TN/TR < 0)", value: "danger", color: "red" },
];

export enum DATA_SYNCING_WEBSOCKET_STATUS {
  COMPLETED = "completed",
  IN_PROGRESS = "in_progress",
  ERROR = "error",
}

export const GOOGLE_GUIDE_STEPS_IMAGES_MAPPER = {
  "1": {
    fileName: "1 Google_IAP_GCP_Project_Select.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/1 Google_IAP_GCP_Project_Select.png",
  },
  "2": {
    fileName: "2 Google_IAP_GCP_Enable_Androidpublisher_API.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/2 Google_IAP_GCP_Enable_Androidpublisher_API.png",
  },
  "3": {
    fileName: "3 Google_IAP_GCP_Enable_DeveloperReporting_API.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/3 Google_IAP_GCP_Enable_DeveloperReporting_API.png",
  },
  "4": {
    fileName: "4 Google_IAP_GCP_Enable_CloudPubSub_API.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/4 Google_IAP_GCP_Enable_CloudPubSub_API.png",
  },
  "5": {
    fileName: "5 Google_IAP_GCP_API_Enable_Verified.jpg",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/5 Google_IAP_GCP_API_Enable_Verified.png",
  },
  "6": {
    fileName: "6 Google_IAP_GCP_CreateServiceAccountMain.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/6 Google_IAP_GCP_CreateServiceAccountMain.png",
  },
  "7": {
    fileName: "7 Google_IAP_GCP_SA_1.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/7 Google_IAP_GCP_SA_1.png",
  },
  "8": {
    fileName: "8 Google_IAP_GCP_SA_2.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/8 Google_IAP_GCP_SA_2.png",
  },
  "9": {
    fileName: "9 Google_IAP_GCP_SA_StorageObject.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/9 Google_IAP_GCP_SA_StorageObject.png",
  },
  "10": {
    fileName: "10 Google_IAP_GCP_SA_MonitoringVeiw.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/10 Google_IAP_GCP_SA_MonitoringVeiw.png",
  },
  "11": {
    fileName: "11 Google_IAP_GCP_SA_PubSubAdmin.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/11 Google_IAP_GCP_SA_PubSubAdmin.png",
  },
  "12": {
    fileName: "12 Google_IAP_GCP_SA_PermissionsSummary.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/12 Google_IAP_GCP_SA_PermissionsSummary.png",
  },
  "13": {
    fileName: "13 Google_IAP_GCP_SA_CreatedSummary.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/13 Google_IAP_GCP_SA_CreatedSummary.png",
  },
  "14": {
    fileName: "14 Google_IAP_PlayConsole_GrantPermission_To_SA.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/14 Google_IAP_PlayConsole_GrantPermission_To_SA.png",
  },
  "15": {
    fileName: "15 Google_IAP_PlayConsole_GrantPermission_To_SA_Invite.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/15 Google_IAP_PlayConsole_GrantPermission_To_SA_Invite.png",
  },
  "15-2": {
    fileName: "15 Google_IAP_PlayConsole_GrantPermission_To_SA_Invite.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/15 Google_IAP_PlayConsole_GrantPermission_To_SA_Permission1.png",
  },
  "16": {
    fileName: "16 Google_IAP_PlayConsole_GrantPermission_To_SA_Permission2.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/16 Google_IAP_PlayConsole_GrantPermission_To_SA_Permission2.png",
  },
  "17": {
    fileName: "17 Google_IAP_GCP_SA_ManageKeys.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/17 Google_IAP_GCP_SA_ManageKeys.png",
  },
  "18": {
    fileName: "18 Google_IAP_GCP_SA_KeysJSON.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/18 Google_IAP_GCP_SA_KeysJSON.png",
  },
  "19": {
    fileName: "19 Google_IAP_GCP_SA_KeysJSONFinal.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/19 Google_IAP_GCP_SA_KeysJSONFinal.png",
  },
  "20": {
    fileName: "20 Google_IAP_Copy_ServiceAccount_Email.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/20 Google_IAP_Copy_ServiceAccount_Email.png",
  },
  "21": {
    fileName: "21 GoogleIAP_Finance_Reports_Cloud_URI_Image.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/21 GoogleIAP_Finance_Reports_Cloud_URI_Image.png",
  },
  "22": {
    fileName: "22 AppRevBooster_GoogleIAP_Integration.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/googleIAPSupportGuideImages/22 AppRevBooster_GoogleIAP_Integration.png",
  },
};

export const APPLE_GUIDE_STEPS_IMAGES_MAPPER = {
  "1": {
    fileName: "1_app-store-connect-api.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/appleIAPSupportGuideImages/1_app-store-connect-api.png",
  },
  "2": {
    fileName: "2_generate-api-key.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/appleIAPSupportGuideImages/2_generate-api-key.png",
  },
  "3": {
    fileName: "3 Apple_IAP_AppStoreConnect_Select.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/appleIAPSupportGuideImages/3_download_keyfile.png",
  },
  "4": {
    fileName: "4_connect-api-key.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/appleIAPSupportGuideImages/4_connect-api-key.png",
  },
  "5": {
    fileName: "5_issuer-id.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/appleIAPSupportGuideImages/5_issuer-id.png",
  },
  "6": {
    fileName: "6_key-id.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/appleIAPSupportGuideImages/6_key-id.png",
  },
  "7": {
    fileName: "7_Vendor_No_ViewPaymentsandProceeds-1_2x-2.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/appleIAPSupportGuideImages/7_Vendor_No_ViewPaymentsandProceeds-1_2x-2.png",
  },
  "8": {
    fileName: "8 Apple_IAP_Integration_ModalPopup.png",
    link: "https://apprevbooster-assets.s3.ap-south-1.amazonaws.com/appleIAPSupportGuideImages/8_Apple_IAP_Integration_ModalPopup.png",
  },
};



export const SPENDING_PLATFORMS: PlatformConfig[] = [
  {
    id: "google_ads",
    name: "Google Ads",
    icon: APP_PLATFORM_LOGO_MAP.google_ads,
    type: "spending",
    authType: "oauth",
    description: "configurations.integrations.google_ads_desc",
  },
  {
    id: "meta_ads",
    name: "Meta Ads",
    icon: APP_PLATFORM_LOGO_MAP.meta,
    type: "spending",
    authType: "oauth",
    description: "configurations.integrations.meta_ads_desc",
    comingSoon: true,
  },
  {
    id: "apple_search_ads",
    name: "Apple Search Ads",
    icon: APP_PLATFORM_LOGO_MAP.app_store,
    type: "spending",
    authType: "oauth",
    description: "configurations.integrations.apple_search_ads_desc",
    comingSoon: true,
  },
];

export const REVENUE_PLATFORMS: PlatformConfig[] = [
  {
    id: "google_admob",
    name: "Google AdMob",
    icon: APP_PLATFORM_LOGO_MAP.google_admob,
    type: "revenue",
    authType: "oauth",
    description: "configurations.integrations.google_admob_desc",
  },
  {
    id: "apple_iap",
    name: "Apple IAP",
    icon: APP_PLATFORM_LOGO_MAP.apple,
    type: "revenue",
    authType: "form",
    description: "configurations.integrations.apple_iap_desc",
  },
  {
    id: "google_iap",
    name: "Google Play IAP",
    icon: APP_PLATFORM_LOGO_MAP.playStore,
    type: "revenue",
    authType: "form",
    description: "configurations.integrations.google_iap_desc",
  },
];

export const DEMO_ACCOUNTS = ["demo1", "demo2"]

export enum SupportTicketsEnum {
  GENERAL = "general",
  TECHNICAL = "technical",
  FEATURE_REQUEST = "feature_request",
  BUG_REPORT = "bug_report",
  ACCOUNT = "account",
  OTHER = "other"
}
